import React, { useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'

import moment from 'moment'
import DatePicker from 'react-datepicker'
import config from '@/config/config-esm'
import { validateFamilyMember } from '@/lib/family_validation'
import PortType from '@/components/PortType'

import 'react-datepicker/dist/react-datepicker.css'

import { setFormData } from '@/store/slices/formDataSlice'

const MemberComponent = ({ member: initialMember, i, toggleEditing: parentToggleEditing }) => {
  const dispatch = useDispatch()
  const formData = useSelector((state) => state.formData)
  const appData = useSelector((state) => state.appData)
  const layout = useSelector((state) => state.layout)
  
  const memberRef = useRef(null)

  const [editing, setEditing] = useState(false)
  const [member, setMember] = useState({
    ...initialMember,
    useOwnerAddress:
      typeof initialMember.useOwnerAddress !== 'undefined' ? initialMember.useOwnerAddress : true,
    extraAbroad: typeof initialMember.extraAbroad !== 'undefined' ? initialMember.extraAbroad : false,
    extraAbroadId: typeof initialMember.extraAbroadId !== 'undefined' ? initialMember.extraAbroadId : false,
  })

  const handleChange = (e) => {
    if (e.target) {
      setMember({
        ...member,
        [e.target.name]: e.target.value,
      })
    } else {
      setMember({
        ...member,
        dateOfBirth: moment(e).format('YYYY-MM-DD'),
      })
    }
  }

  const valid = () => {
    return validateFamilyMember(member)
  }

  const save = () => {
    if (!validateFamilyMember(member)) {
      return false
    }

    const members = [...formData.family.members]
    members[i] = {
      ...member,
      set: true,
    }

    dispatch(
      setFormData({
        family: {
          ...formData.family,
          members: members,
        },
      }),
    )

    setMember({
      ...members[i],
      set: true,
    })
    toggleEditing()
  }

  const deleteMember = (idx) => {
    const members = [...formData.family.members]
    members.splice(idx, 1)

    const counts = {
      ...formData.family.counts,
      [member.type]: formData.family.counts[member.type] - 1,
    }

    dispatch(
      setFormData({
        family: {
          ...formData.family,
          members: members,
          counts: counts,
        },
      }),
    )
  }

  const toggleEditing = () => {
    const newEditingState = !editing

    setEditing(newEditingState)
    
    if (newEditingState === true) {
      memberRef.current?.scrollIntoView({
        behavior: 'smooth',
      })
    }

    parentToggleEditing(newEditingState)
  }

  if (!formData.family.members[i]) return null

  const { ownerWantsSubscription } = formData.family

  const isOwner = member.owner

  const typeName = member.type === 'child' ? 'Barn' : 'Familiemedlem'
  const name = formData.family.members[i].firstName
    ? `${formData.family.members[i].firstName} ${formData.family.members[i].lastName}`
    : typeName
  const familyExtraServices = appData.familyExtraServices || []
  const filteredFamilyExtraServices = config.campaignsAsExtraServices
    .map((c) => {
      return { id: c[1], name: c[0], active: true }
    })
    .concat(familyExtraServices)
    .filter((s) => s.active && s.id !== config.digitalSafetyId)

  return (
      <div
        ref={memberRef}
        key={`member-${i}`}
        className={
          isOwner
            ? 'memberContainer owner'
            : member.set
              ? 'memberContainer'
              : editing
                ? 'memberContainer unset editing'
                : 'memberContainer unset'
        }
      >
        <div className="main">
          <div className="person">
            <div className="name">{name}</div>
            <div className="age">
              {member.dateOfBirth
                ? `${typeName}, ${moment().toDate().getFullYear() - moment(member.dateOfBirth).toDate().getFullYear() + ' år'}`
                : isOwner
                  ? 'Sjefen'
                  : 'Knytt til person'}
              {member.owner && ownerWantsSubscription && (
                <div className="ownerWantsSubscriptionText">Skal ha eget abonnement</div>
              )}
              {member.owner && !ownerWantsSubscription && (
                <div className="ownerWantsSubscriptionText">Skal ikke ha eget abonnement</div>
              )}
            </div>
          </div>
          {isOwner && (
            <button
              className="icon edit-item"
              onClick={() => dispatch(setFormData({ familyStep: 'owner' }))}
            ></button>
          )}
          {!isOwner && (
            <div style={{ textAlign: 'right' }}>
              <button
                className={`icon delete-item${editing ? ' editing' : ''}`}
                onClick={() => deleteMember(i)}
              ></button>
              <button
                className={`icon edit-item${editing ? ' editing' : ''}`}
                onClick={toggleEditing}
              ></button>
            </div>
          )}
        </div>
        {editing && (
          <div className="editingContainer">
            <div className="input_wrapper">
              <label htmlFor="firstName">Fornavn og mellomnavn:</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={member.firstName || ''}
                onChange={handleChange}
              />
            </div>

            <div className="input_wrapper">
              <label htmlFor="lastName">Etternavn:</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={member.lastName || ''}
                onChange={handleChange}
              />
            </div>

            <div className="input_wrapper">
              <label htmlFor="lastName">Telefonnr:</label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={member.phone || ''}
                onChange={handleChange}
              />
            </div>

            <div className="input_wrapper">
              <label htmlFor="dateofbirth">Fødselsdato:</label>
              <DatePicker
                name="dateOfBirth"
                dateFormat="dd/MM/yyyy"
                className="form-control"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={moment().toDate()}
                selected={
                  member.dateOfBirth
                    ? moment(member.dateOfBirth).toDate()
                    : null
                }
                onChange={handleChange}
              />
            </div>

            <div className="input_wrapper">
              <label htmlFor="email">E-post:</label>
              <input
                type="text"
                id="email"
                name="email"
                value={member.email || ''}
                onChange={handleChange}
              />
            </div>

            {member.type === 'child' && (
              <div className="input_wrapper more-spacing less-bottom-spacing">
                <input
                  type="checkbox"
                  value={member.childMode}
                  id="child_mode"
                  checked={member.childMode}
                  onChange={(e) => {
                    setMember({
                      ...member,
                      childMode: e.target.checked,
                    })
                  }}
                />
                <label htmlFor="child_mode">
                  <span>Barnemodus</span>
                </label>
              </div>
            )}

            <React.Fragment>
              <h3>Tilleggstjenester for familiemedlem</h3>
              {filteredFamilyExtraServices.map((svc) => {
                const extraServices = member.extraServices || []
                const existingIdx = extraServices.findIndex((s) => s === svc.id)
                return (
                  <div className="input_wrapper" key={svc.id}>
                    <input
                      type="checkbox"
                      value={1}
                      id={`member_${svc.id}`}
                      checked={existingIdx !== -1}
                      onChange={(e) => {
                        const newExtraServices = e.target.checked
                          ? existingIdx === -1
                            ? extraServices.concat([svc.id])
                            : extraServices
                          : existingIdx === -1
                            ? extraServices
                            : extraServices
                                .slice(0, existingIdx)
                                .concat(extraServices.slice(existingIdx + 1))
                        setMember({
                          ...member,
                          extraServices: newExtraServices,
                        })
                      }}
                    />
                    <label htmlFor={`member_${svc.id}`}>
                      <span>{svc.name}</span>
                    </label>
                  </div>
                )
              })}
            </React.Fragment>

            <div className="input_wrapper more-spacing">
              <div className="input_wrapper">
                <input
                  type="radio"
                  value={true}
                  id="use-owner-address"
                  checked={member.useOwnerAddress === true}
                  onClick={(e) => {
                    setMember({
                      ...member,
                      useOwnerAddress: true,
                      address: '',
                      postalCode: '',
                      postalArea: '',
                    })
                  }}
                  onChange={(e) => {}}
                />
                <label htmlFor="use-owner-address">
                  <span>Bruk samme adresse som eier</span>
                </label>
              </div>

              <div className="input_wrapper">
                <input
                  type="radio"
                  value={false}
                  id="use-own-address"
                  checked={member.useOwnerAddress === false}
                  onClick={(e) => {
                    setMember({
                      ...member,
                      useOwnerAddress: false,
                      address: '',
                      postalCode: '',
                      postalArea: '',
                    })
                  }}
                  onChange={(e) => {}}
                />
                <label htmlFor="use-own-address">
                  <span>Har egen adresse</span>
                </label>
              </div>
            </div>

            {member.useOwnerAddress === false && (
              <div>
                <div className="input_wrapper">
                  <label htmlFor="address">Gateadresse:</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={member.address}
                    onChange={handleChange}
                  />
                </div>

                <div className="input_wrapper">
                  <label htmlFor="postalCode">Postnummer:</label>
                  <input
                    type="text"
                    maxLength="4"
                    id="postalCode"
                    name="postalCode"
                    value={member.postalCode}
                    onChange={handleChange}
                  />
                </div>

                <div className="input_wrapper">
                  <label htmlFor="postalArea">Poststed:</label>
                  <input
                    type="text"
                    id="postalArea"
                    name="postalArea"
                    value={member.postalArea}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            <PortType 
              person={member} 
              onPersonChange={(updatedMember) => setMember(updatedMember)} 
            />

            <div className="buttons">
              <button className="cancel" onClick={toggleEditing}>
                Avbryt
              </button>
              <button className="save right" disabled={!valid()} onClick={save}>
                Lagre
              </button>
            </div>
          </div>
        )}
      </div>
  )
}

export default MemberComponent
